/**
 * Popup ad — shown as a dismissible modal, once per browser session per ad.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, Sparkles, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Ad, trackAdClick, trackAdImpression } from "@/hooks/useAds";

interface AdPopupProps {
  ad: Ad;
  onClose: () => void;
}

const AdPopup = ({ ad, onClose }: AdPopupProps) => {
  const navigate = useNavigate();

  useEffect(() => {
    trackAdImpression(ad.id);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad.id]);

  const handleCtaClick = () => {
    trackAdClick(ad.id);
    const isExternal = ad.link_url?.startsWith("http");
    onClose();
    if (isExternal) {
      window.open(ad.link_url as string, "_blank", "noopener,noreferrer");
    } else {
      navigate(ad.link_url || "/");
    }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-sm sm:max-w-md overflow-hidden rounded-2xl bg-card border border-border shadow-2xl"
        >
          <button
            onClick={onClose}
            aria-label="Close ad"
            className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {ad.image_url && (
            <div className="w-full h-40 sm:h-48 overflow-hidden bg-muted">
              <img
                src={ad.image_url}
                alt={ad.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-4 sm:p-6">
            <div className="inline-flex items-center gap-1 bg-primary/10 text-primary text-[10px] font-medium px-2 py-0.5 rounded-full mb-2">
              <Sparkles className="w-3 h-3" />
              <span>Sponsored</span>
            </div>

            <h3 className="text-base sm:text-lg font-bold text-foreground mb-1.5">
              {ad.title}
            </h3>

            {ad.description && (
              <p className="text-muted-foreground text-sm mb-4">{ad.description}</p>
            )}

            <button
              onClick={handleCtaClick}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-full text-sm font-medium transition-colors"
            >
              Learn More
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default AdPopup;
